import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Alert, Modal, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Image } from 'expo-image';
import { useNavigation } from '@react-navigation/native';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import QRCode from 'react-native-qrcode-svg';
import * as FileSystem from 'expo-file-system/legacy';
import Share from 'react-native-share';
import { useTheme } from '../../theme/ThemeContext';
import { useAuth } from '../../context/AuthContext';

interface BusinessCardProps {
  userData?: any; // Optional user data override
  footerContent?: React.ReactNode; // Optional footer content (e.g., connection buttons)
  topRightContent?: React.ReactNode; // Optional top-right content (e.g., admin menu)
  onPress?: () => void; // Optional card press handler
}

const GOLD_GRADIENT_COLORS_DARK = ['#050505', '#0E0B08', '#1D140B', '#2F1E10', '#B88632'] as const;
const GOLD_GRADIENT_COLORS_LIGHT = ['#F5F1E8', '#EDE7D6', '#E5D9B8', '#D9C89A', '#B88632'] as const;
const GOLD_GRADIENT_LOCATIONS = [0, 0.35, 0.58, 0.78, 1] as const;
const GOLD_GLOW_COLOR = 'rgba(216, 173, 95, 0.25)';

const BusinessCard: React.FC<BusinessCardProps> = ({ userData, footerContent, topRightContent, onPress }) => {
  const { theme, isDark } = useTheme();
  const { user: authUser } = useAuth();
  const [showQRModal, setShowQRModal] = useState(false);
  const navigation = useNavigation<any>();

  // Use provided userData or fall back to authenticated user
  const user = userData || authUser;

  if (!user) {
    return null;
  }

  const handleShare = async () => {
    try {
      console.log('🟡 [BusinessCard] Starting share...');
      const userId = user?.id || user?.userId;
      console.log('🟡 User ID:', userId);
      console.log('🟡 User name:', user?.name || user?.fullName);
      console.log('🟡 Has preview image:', !!user?.previewImageUrl);
      console.log('🟡 Preview image URL:', user?.previewImageUrl);
      console.log('🟡 Platform:', Platform.OS);
      
      if (!userId) {
        console.log('❌ No user ID found');
        Alert.alert('Error', 'Unable to share profile');
        return;
      }

      const profileUrl = `https://lnbapp.ln.ink/profile/${userId}`;
      
      const shareMessage = `Hey! 🌟 Check out ${user.name || user.fullName}'s profile on LNB - Leads n Business for Business!\n\nConnect with entrepreneurs, grow your network, and build meaningful business relationships.\n\n${profileUrl}`;

      // If user has a preview image (gold card), download and share it
      if (user?.previewImageUrl && Platform.OS !== 'web') {
        const fileUri = `${FileSystem.cacheDirectory}profile_${userId}.png`;
        console.log('🟡 Attempting to download image...');
        console.log('🟡 Download URL:', user.previewImageUrl);
        console.log('🟡 Save to:', fileUri);
        
        try {
          const downloadResult = await FileSystem.downloadAsync(user.previewImageUrl, fileUri);
          console.log('🟡 Download result status:', downloadResult.status);
          console.log('🟡 Download result URI:', downloadResult.uri);
          
          if (downloadResult.status === 200) {
            console.log('✅ Image downloaded successfully!');
            
            // Share image with message using react-native-share
            await Share.open({
              title: `${user.name || user.fullName}'s Profile`,
              message: shareMessage,
              url: Platform.OS === 'android' ? `file://${downloadResult.uri}` : downloadResult.uri,
              type: 'image/png',
            });
            console.log('✅ Profile shared successfully with image');
            return;
          } else {
            console.log('❌ Download failed with status:', downloadResult.status);
          }
        } catch (imageError: any) {
          if (imageError.message !== 'User did not share') {
            console.error('❌ Error downloading/sharing preview image:', imageError);
          }
        }
      } else {
        console.log('⚠️ Skipping image download:', {
          hasPreviewUrl: !!user?.previewImageUrl,
          isWeb: Platform.OS === 'web'
        });
      }

      // Fallback: Share just the message with URL
      console.log('🟡 Falling back to message-only share');
      await Share.open({
        message: shareMessage,
      });
    } catch (error: any) {
      console.error('❌ Error sharing profile:', error);
      Alert.alert('Error', 'Failed to share profile');
    }
  };

  const handleQRPress = () => {
    setShowQRModal(true);
  };

  const handleCardPress = () => {
    // Navigate to the user's profile with their userId
    if (user?.id) {
      navigation.navigate('Profile', { userId: user.id });
    } else {
      navigation.navigate('Profile');
    }
  };

  const handleBusinessPress = (e: any) => {
    e.stopPropagation();
    // Navigate to the user's profile and show business details
    if (user?.id) {
      navigation.navigate('Profile', { userId: user.id, showBusiness: true });
    } else {
      navigation.navigate('Profile', { showBusiness: true });
    }
  };

  const getInitials = () => {
    if (!user.name) return '?';
    const parts = user.name.trim().split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <TouchableOpacity 
      style={styles.businessCardContainer} 
      onPress={onPress || handleCardPress}
      activeOpacity={0.85}
    >
      <LinearGradient
        colors={isDark ? GOLD_GRADIENT_COLORS_DARK : GOLD_GRADIENT_COLORS_LIGHT}
        locations={GOLD_GRADIENT_LOCATIONS}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      >
        <View style={styles.cardDecorLayer}>
          <View style={[styles.glowOrb, styles.glowOrbTop]} />
          <View style={[styles.glowOrb, styles.glowOrbBottom]} />
        </View>
      </LinearGradient>

      <View style={{ padding: 24, zIndex: 1 }}>
        {topRightContent && (
          <View style={styles.topRightContainer}>
            {topRightContent}
          </View>
        )}
        <View style={styles.cardHeaderRow}>
          <View style={styles.cardAvatar}>
            <View style={[styles.avatar, { backgroundColor: theme.primary }]}>
              {(user as any).photo ? (
                <Image 
                  source={{ uri: (user as any).photo }} 
                  style={styles.avatarImage}
                  contentFit="cover"
                />
              ) : (
                <Text style={styles.initials}>{getInitials()}</Text>
              )}
            </View>
          </View>
          
          <View style={styles.cardTitleBlock}>
            <Text style={[styles.cardName, { color: isDark ? '#FFFFFF' : '#1A1A1A' }]}>{user.name || 'Unknown User'}</Text>
            {(user as any).designation && (
              <Text 
                style={[styles.cardProfession, { color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.75)' }]}
                numberOfLines={2}
              >
                {(user as any).company 
                  ? `${(user as any).designation} at ${(user as any).company}`
                  : (user as any).designation
                }
              </Text>
            )}
            <View style={styles.infoRow}>
              {(user as any).industry && (
                <View style={[styles.infoPill, { backgroundColor: isDark ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.4)' }]}>
                  <MaterialCommunityIcons name="tag" size={12} color={isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.7)'} />
                  <Text style={[styles.infoPillText, { color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.7)' }]}>{(user as any).industry}</Text>
                </View>
              )}
              {(user as any).city && (
                <View style={[styles.infoPill, { backgroundColor: isDark ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.4)' }]}>
                  <Ionicons name="location" size={12} color={isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.7)'} />
                  <Text style={[styles.infoPillText, { color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.7)' }]}>{(user as any).city}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {((user as any).businessName || (user as any).businessIndustry || (user as any).businessTitle) ? (
          <TouchableOpacity 
            style={styles.businessSection} 
            onPress={handleBusinessPress}
            activeOpacity={0.7}
          >
            <View style={[styles.businessBadge, { 
              backgroundColor: isDark ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.4)',
              borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
            }]}>
              <View style={[styles.businessBadgeIcon, { backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' }]}>
                {(user as any).businessPhoto ? (
                  <Image 
                    source={{ uri: (user as any).businessPhoto }} 
                    style={styles.businessLogoImage}
                    contentFit="cover"
                  />
                ) : (
                  <MaterialCommunityIcons name="briefcase" size={24} color={isDark ? '#FFFFFF' : '#1A1A1A'} />
                )}
              </View>
              <View style={styles.businessBadgeText}>
                <Text style={[styles.businessBadgeTitle, { color: isDark ? '#FFFFFF' : '#1A1A1A' }]}>
                  {(user as any).businessName || (user as any).businessTitle || 'Business'}
                </Text>
                <Text style={[styles.businessBadgeSubtitle, { color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.65)' }]}>
                  {(user as any).businessIndustry || 'Industry'}
                </Text>
              </View>
            </View>
            <View style={styles.actionButtons}>
              <TouchableOpacity 
                style={styles.qrButton} 
                onPress={(e) => {
                  e.stopPropagation();
                  handleQRPress();
                }}
              >
                <Ionicons name="qr-code-outline" size={20} color={theme.primary} />
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.shareButton} 
                onPress={(e) => {
                  e.stopPropagation();
                  handleShare();
                }}
              >
                <Ionicons name="share-outline" size={22} color={theme.primary} />
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        ) : (
          <View style={styles.actionButtonsRow}>
            <TouchableOpacity 
              style={styles.qrButton} 
              onPress={(e) => {
                e.stopPropagation();
                handleQRPress();
              }}
            >
              <Ionicons name="qr-code-outline" size={20} color={theme.primary} />
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.shareButton} 
              onPress={(e) => {
                e.stopPropagation();
                handleShare();
              }}
            >
              <Ionicons name="share-outline" size={22} color={theme.primary} />
            </TouchableOpacity>
          </View>
        )}

        {/* Optional footer content (e.g., connection buttons for profiles) */}
        {footerContent && footerContent}
      </View>

      {/* QR Modal */}
      <Modal
        visible={showQRModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowQRModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.qrModalContent, { backgroundColor: theme.backgroundSecondary }]}>
            <View style={styles.qrModalHeader}>
              <Text style={[styles.qrModalTitle, { color: theme.text }]}>Quick Connect</Text>
              <TouchableOpacity onPress={() => setShowQRModal(false)}>
                <Ionicons name="close-circle" size={28} color={theme.textSecondary} />
              </TouchableOpacity>
            </View>
            
            <View style={styles.qrContainer}>
              <View style={[styles.qrCodeWrapper, { backgroundColor: '#FFFFFF' }]}>
                <QRCode
                  value={`https://lnbapp.ln.ink/profile/${user?.id || user?.userId}`}
                  size={220}
                  color="#000000"
                  backgroundColor="#FFFFFF"
                  logo={require('../../../assets/app-icons.png')}
                  logoSize={50}
                  logoBackgroundColor="#FFFFFF"
                />
              </View>
              
              <View style={styles.qrUserInfo}>
                <Text style={[styles.qrUserName, { color: theme.text }]}>
                  {user?.name || 'User'}
                </Text>
                {(user as any)?.designation && (
                  <Text style={[styles.qrUserTitle, { color: theme.textSecondary }]}>
                    {(user as any).designation}
                  </Text>
                )}
              </View>
            </View>

            <Text style={[styles.qrInstructions, { color: theme.textSecondary }]}>
              Scan this QR code to view profile
            </Text>
          </View>
        </View>
      </Modal>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  businessCardContainer: {
    borderRadius: 28,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 18 },
    shadowOpacity: 0.4,
    shadowRadius: 24,
    elevation: 18,
    overflow: 'hidden',
    position: 'relative',
  },
  topRightContainer: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 10,
  },
  cardDecorLayer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  glowOrb: {
    position: 'absolute',
    width: 220,
    height: 220,
    borderRadius: 110,
    backgroundColor: GOLD_GLOW_COLOR,
    opacity: 0.85,
  },
  glowOrbTop: {
    top: -60,
    right: -80,
  },
  glowOrbBottom: {
    bottom: -90,
    left: -40,
  },
  cardHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  cardAvatar: {
    marginRight: 18,
  },
  avatar: {
    width: 96,
    height: 96,
    borderRadius: 48,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: 'rgba(255,255,255,0.3)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  initials: {
    fontSize: 32,
    fontWeight: '700',
    fontFamily: 'ComicNeue_700Bold',
    color: '#000000',
  },
  actionButtons: {
    flexDirection: 'column',
    gap: 8,
  },
  actionButtonsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    marginTop: 16,
    paddingHorizontal: 40,
  },
  qrButton: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: 'rgba(255,255,255,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  shareButton: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: 'rgba(255,255,255,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  cardTitleBlock: {
    flex: 1,
  },
  cardName: {
    fontSize: 28,
    fontWeight: '700',
    fontFamily: 'ComicNeue_700Bold',
    marginBottom: 6,
    textShadowColor: 'rgba(0, 0, 0, 0.15)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 4,
  },
  cardProfession: {
    fontSize: 14,
    fontWeight: '500',
    fontFamily: 'ComicNeue_400Regular',
    marginBottom: 8,
    lineHeight: 18,
  },
  infoRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  infoPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
  },
  infoPillText: {
    marginLeft: 4,
    fontSize: 11,
    fontFamily: 'ComicNeue_400Regular',
  },
  industryPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.25)',
    marginTop: 10,
    alignSelf: 'flex-start',
  },
  industryPillText: {
    marginLeft: 6,
    fontSize: 12,
    fontFamily: 'ComicNeue_400Regular',
    color: 'rgba(255,255,255,0.9)',
  },
  cardDivider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.15)',
    marginTop: 0,
    marginBottom: 18,
  },
  businessSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 16,
  },
  businessBadge: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 20,
    padding: 16,
    borderWidth: 1,
  },
  businessBadgeIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
    overflow: 'hidden',
  },
  businessLogoImage: {
    width: '100%',
    height: '100%',
  },
  businessBadgeText: {
    flex: 1,
  },
  businessBadgeTitle: {
    fontSize: 16,
    fontWeight: '700',
    fontFamily: 'ComicNeue_700Bold',
    marginBottom: 2,
  },
  businessBadgeSubtitle: {
    fontSize: 13,
    fontFamily: 'ComicNeue_400Regular',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  qrModalContent: {
    width: '85%',
    borderRadius: 24,
    padding: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 20,
    elevation: 10,
  },
  qrModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: 24,
  },
  qrModalTitle: {
    fontSize: 22,
    fontWeight: '700',
    fontFamily: 'ComicNeue_700Bold',
  },
  qrContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  qrCodeWrapper: {
    padding: 12,
    borderRadius: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
  },
  qrUserInfo: {
    alignItems: 'center',
  },
  qrUserName: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  qrUserTitle: {
    fontSize: 14,
  },
  qrInstructions: {
    fontSize: 13,
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default BusinessCard;

